/**
 * VqeUnavailableIndicator — the never-silent teaching indicator (VQE Polish,
 * EDG-16/18/20/21/23 + genuine parse errors). Stands in for the hidden edit
 * pencil when the cursor statement can't be edited visually, naming the
 * construct and why in its tooltip per the governing directive (what · why ·
 * what next). It is a PASSIVE, non-interactive glyph — deliberately NOT a
 * disabled pencil that looks clickable — so the user reads "the visual editor
 * saw this and stepped aside", not "a broken button".
 *
 * Two variants share one copy source (`vqeCopy.unavailable`):
 *   - `'gutter'` — icon-only muted square, sized to the editor line height so it
 *     reads as a gutter glyph beside the Execute/Peek stack.
 *   - `'inline'` — icon + terse caption chip, sitting in the floating
 *     cursor-statement bar next to Execute/Peek.
 *
 * Passive display (tooltip only) ⇒ no analytics event (no user action fires).
 */
import { Info } from 'lucide-react';

import { VqeTooltip } from '../render/common/VqeTooltip';
import { vqeCopy } from '../ux/vqeCopy';
import type { VqeUnavailableReason } from './vqeEditAffordance';

type NonNullReason = Exclude<VqeUnavailableReason, null>;

export interface VqeUnavailableIndicatorProps {
  readonly reason: NonNullReason;
  readonly variant: 'gutter' | 'inline';
  /** Gutter square size in px (defaults so callers without layout still render). */
  readonly sizePx?: number;
  /** Icon size in px. */
  readonly iconSize?: number;
}

function tooltipFor(reason: NonNullReason): string {
  switch (reason) {
    case 'parse-error':
      return vqeCopy.unavailable.parseError();
    case 'procedural':
      return vqeCopy.unavailable.procedural;
    case 'set-op-precedence':
      return vqeCopy.unavailable.setOpPrecedence;
    case 'graph-table':
      return vqeCopy.unavailable.graphTable;
  }
}

function labelFor(reason: NonNullReason): string {
  switch (reason) {
    case 'parse-error':
      return vqeCopy.unavailable.label.parseError;
    case 'procedural':
      return vqeCopy.unavailable.label.procedural;
    case 'set-op-precedence':
      return vqeCopy.unavailable.label.setOpPrecedence;
    case 'graph-table':
      return vqeCopy.unavailable.label.graphTable;
  }
}

export function VqeUnavailableIndicator({
  reason,
  variant,
  sizePx,
  iconSize = 12,
}: VqeUnavailableIndicatorProps) {
  const tooltip = tooltipFor(reason);

  if (variant === 'gutter') {
    return (
      <VqeTooltip content={tooltip} selfProvider>
        <div
          role="note"
          aria-label={tooltip}
          data-testid="vqe-unavailable-indicator"
          data-reason={reason}
          data-variant="gutter"
          style={sizePx ? { height: sizePx, width: sizePx } : undefined}
          className="flex items-center justify-center rounded-sm cursor-default select-none
            bg-muted/60 text-muted-foreground border border-border/70 backdrop-blur-sm shadow-sm"
        >
          <Info style={{ height: iconSize, width: iconSize }} />
        </div>
      </VqeTooltip>
    );
  }

  return (
    <VqeTooltip content={tooltip} selfProvider>
      <div
        role="note"
        aria-label={tooltip}
        data-testid="vqe-unavailable-indicator"
        data-reason={reason}
        data-variant="inline"
        className="flex items-center gap-1.5 px-2.5 py-[2px] text-xs font-medium rounded-md
          cursor-default select-none bg-muted/70 text-muted-foreground border border-border
          backdrop-blur-sm shadow-md"
      >
        <Info style={{ height: iconSize, width: iconSize }} />
        {labelFor(reason)}
      </div>
    </VqeTooltip>
  );
}
